import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentLang, withLang } from "../i18n/LanguageContext";
import { apiErrorMessage } from "../lib/apiError";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    console.log('[AuthContext] Checking auth status...');
    try {
      const response = await fetch(`${backendUrl}/api/profile`, {
        credentials: 'include', // Include cookies
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('[AuthContext] User authenticated:', userData.email, 'role:', userData.role);
        setUser(userData);
        return userData; // Return user data for use in callback
      } else {
        console.log('[AuthContext] Not authenticated, status:', response.status);
        setUser(null);
        return null;
      }
    } catch (error) {
      console.error('[AuthContext] Error checking auth status:', error);
      setUser(null);
      return null;
    } finally {
      console.log('[AuthContext] Auth check complete, setting loading=false');
      setLoading(false);
    }
  };

  const requestMagicLink = async (email) => {
    try {
      const response = await fetch(`${backendUrl}/api/auth/request-magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, lang: getCurrentLang() }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(apiErrorMessage(data, 'Ошибка при отправке ссылки'));
      }

      return { success: true, message: data.message };
    } catch (error) {
      console.error('Error requesting magic link:', error);
      return { 
        success: false, 
        message: error.message || 'Произошла ошибка при отправке ссылки для входа' 
      };
    }
  };

  const clearClientState = () => {
    // Clear all auth-related localStorage keys
    localStorage.removeItem('loginRedirect');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('session');
    // Clear all auth-related sessionStorage keys
    sessionStorage.clear();
  };

  const logout = async () => {
    // Clear client state first
    setUser(null);
    clearClientState();
    
    // Also clear the session cookie from JavaScript side
    // This covers cases where the server response Set-Cookie doesn't take effect
    document.cookie = 'session=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
    document.cookie = 'session=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=eunoiaspace.eu;';
    document.cookie = 'session=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.eunoiaspace.eu;';
    
    try {
      // Call the logout API and WAIT for response (so Set-Cookie is processed)
      await fetch(`${backendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error calling logout endpoint:', error);
    }
    
    // Redirect after server has cleared the cookie
    window.location.href = withLang('/', getCurrentLang());
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await fetch(`${backendUrl}/api/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        // FastAPI 422 returns `detail` as an array of error objects.
        // Surface the first message in a human-readable way.
        let msg = 'Ошибка при обновлении профиля';
        if (typeof data?.detail === 'string') {
          msg = data.detail;
        } else if (Array.isArray(data?.detail) && data.detail.length > 0) {
          const first = data.detail[0];
          const field = Array.isArray(first?.loc) ? first.loc.slice(-1)[0] : '';
          if (field === 'bio' && (first?.msg || '').toLowerCase().includes('1000')) {
            msg = 'Слишком длинное «О себе» — максимум 1000 символов';
          } else if (field) {
            msg = `Ошибка поля «${field}»: ${first?.msg || msg}`;
          } else {
            msg = first?.msg || msg;
          }
        }
        throw new Error(msg);
      }

      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { 
        success: false, 
        message: error.message || 'Произошла ошибка при обновлении профиля' 
      };
    }
  };

  const value = {
    user,
    loading,
    requestMagicLink,
    logout,
    updateProfile,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};